import { t } from 'i18next';
import { Float32BufferAttribute, Points, Ray, Raycaster, Vector3 } from 'three';

import { ContextMenuItem } from '../../../../../../../react/components/skyview/context-menu';
import { DesignFeature } from '../../../../../../../react/components/skyview/right-menu/tools/design-tool-menu/sections/design-feature';
import { slopePercentToHeightDiff } from '../../../../../../../utils/math/slope';
import { isDefined } from '../../../../../../../utils/variables';
import cameraManager from '../../../../../../camera-manager';
import { SkyviewCamera } from '../../../../../../cameras/types';
import { CoordinateManager } from '../../../../../../coordinate-manager';
import { RevertChange } from '../../../../../../elements/design/design-node/design-node';
import {
  getConicDirection,
  getConicParameter,
  getConicPoint,
  getSegmentConic,
  getSegmentCurve,
  reverseSegmentCurve,
  SegmentCurve,
} from '../../../../../../elements/design/design-node/utils/conic';
import {
  findLineSegmentIdFromIntersection,
  LineSegmentId,
} from '../../../../../../elements/design/design-node/utils/line-segment-node';
import { addPoints, PointId } from '../../../../../../elements/design/design-node/utils/point-node';
import { publish, SubscriptionTopic } from '../../../../../../messaging/pubsub';
import {
  areVectorsParallel,
  checkLine2dIntersection,
  findClosestPointOnLine,
  findClosestPointOnRay,
  findPointOnLineFromHeight,
  getQuaternionBetweenVectors,
  isValidVector3,
  toVector3,
} from '../../../../../../utilities/math-helpers';
import { ActionName, DesignTool, PlaceMode, SnapMode } from '../../../../../design-tool';
import { PickingLine } from '../../../../../picking-line';
import { interpolateMissingValues } from '../../../../interpolate-missing-values';
import { addAndCommitAction } from '../../utils/actions';
import { getLineCursorVertices } from '../../utils/curve';
import { listLinesAndPolygonsBySelection } from '../../utils/multi-select';
import { deselect, getPointIdArray, selectLines } from '../../utils/select';
import { abortActionInProgress, initActionInProgress, quickSelectLineOrPolygon } from '../general';

/**
 * Update the offset line with the new vertices, curved where the line segments have curves.
 */
function refreshOffsetLine(
  offsetLine: PickingLine,
  worldPositions: Vector3[],
  isPolygon: boolean,
  curves: (SegmentCurve | undefined)[],
) {
  const segmentCount = isPolygon ? worldPositions.length : worldPositions.length - 1;
  const offsetLineVertices = worldPositions
    .slice(0, segmentCount)
    .flatMap((val, idx) =>
      getLineCursorVertices(val, worldPositions[(idx + 1) % worldPositions.length], curves[idx]),
    );

  offsetLine.setNonIndexedVertices(offsetLineVertices);
}

const activateCreateOffsetFeature = (tool: DesignTool): ContextMenuItem => {
  return {
    title: t('contextMenu.functions.items.createOffset', { ns: 'skyviewDesign' }),
    action: () => {
      const selectedLineSegmentId = findLineSegmentIdFromIntersection(
        tool.asset.designNode,
        tool.intersection,
      );

      // If multi select is not already in progress automatically selet the line over which
      // the user is hovering the mouse cursor.
      if (isDefined(selectedLineSegmentId) && tool.currentSelection.lineSegments.length === 0) {
        quickSelectLineOrPolygon(tool);
      } else {
        // Trigger multiselect updated event so the ui is triggered.
        // if it is already opened.
        publish(SubscriptionTopic.DesignToolMultiselectUpdated, undefined);
      }

      tool.autoTriggerOffset = true;
      tool.activeFeature = DesignFeature.CreateOffset;
      publish(SubscriptionTopic.DesignToolMenuStateChanged, {
        designMode: false,
        features: true,
      });

      tool.contextMenuCallback?.();
    },
    hotkey: 'O',
  };
};

function initCreateOffset(
  tool: DesignTool,
  segments: LineSegmentId[],
  setShowProgressDialogCallback: (state: boolean) => void,
) {
  const offsetLine = new PickingLine();
  /**
   * The result in local positions.
   */
  let offsetResult: Vector3[] = [];
  /**
   * The curves of the offset line segments.
   */
  let offsetCurves: (SegmentCurve | undefined)[] = [];

  let isPolygon: boolean;
  /**
   * The curves of the line segments of the selected polyline / polygon.
   */
  let curves: (SegmentCurve | undefined)[];

  /**
   * An array with the geometry indices of each point on the selected polyline / polygon.
   */
  let pointIdArray: PointId[];
  /**
   * The local position in the scene of each point in the selected polyline / polygon.
   */
  let localPositions: Vector3[];

  initActionInProgress(tool, {
    actionName: ActionName.Offset,
    initiate: () => {
      const selection = listLinesAndPolygonsBySelection(tool.asset.designNode, segments);
      const result = getOffsetConfiguration(
        tool,
        selection[0].lines.map((x) => x.id),
      );
      deselect(tool);
      selectLines(tool, result.segments);
      pointIdArray = result.pointIdArray;

      localPositions = result.positions;

      isPolygon = result.isPolygon;

      curves = result.curves;

      tool.add(offsetLine);

      refreshOffsetLine(
        offsetLine,
        result.positions.map((x) => tool.asset.designNode.getWorldPosition(x)),
        isPolygon,
        curves,
      );
    },
    cleanup: () => {
      tool.remove(offsetLine);
    },
    refresh: () => {
      const result = generateOffset(tool, pointIdArray, localPositions, isPolygon);
      if (!isDefined(result)) {
        // The settings can make the offset invalid, e.g. a 3D length that is shorter than the
        // height difference, so the previous offset can no longer be applied.
        offsetResult = [];
        offsetLine.visible = false;

        return;
      }

      offsetResult = result.map((x) => tool.asset.designNode.getLocalPosition(x));
      offsetCurves = getOffsetCurves(localPositions, curves, offsetResult);
      offsetLine.visible = true;
      offsetLine.position.set(0, 0, 0);
      refreshOffsetLine(offsetLine, result, isPolygon, offsetCurves);
    },
    readyToApply: () => offsetResult.length > 0,
    tooltipPoint: () => getOffsetTooltipPoint(tool, offsetResult, localPositions),
    apply: async () => {
      if (tool.placeMode === PlaceMode.IntersectPointcloud) {
        await abortActionInProgress(tool).action();
        setShowProgressDialogCallback(true);
      } else {
        applyOffsetResult(tool, offsetResult, isPolygon, offsetCurves);
      }
    },
  });
}

/**
 * Gets what the tooltip is about while an offset is created and the cursor isn't snapped to
 * anything: the vertex of the offset closest to the cursor, or without an offset, e.g. with
 * settings that can't be used together, the cursor at the height of the line that is offset.
 */
function getOffsetTooltipPoint(
  tool: DesignTool,
  offsetResult: Vector3[],
  localPositions: Vector3[],
): Vector3 | undefined {
  const camera = cameraManager().getActiveCamera();
  const pointer = tool.lastMouseEvent?.pointer.position;

  if (!camera || !pointer || localPositions.length === 0) {
    return undefined;
  }

  const { ray } = camera.getRay(pointer.x, pointer.y);
  const node = tool.asset.designNode;

  if (offsetResult.length === 0) {
    const height = node.getWorldPosition(localPositions[0]).z;
    return findPointOnLineFromHeight(ray.origin, ray.direction, height);
  }

  return offsetResult
    .map((x) => node.getWorldPosition(x))
    .reduce((closest, x) =>
      ray.distanceSqToPoint(x) < ray.distanceSqToPoint(closest) ? x : closest,
    );
}

/**
 * Returns a list of indices which to base the offset off from.
 * The result is sorted to prioritize segments that are close to the cursor and does
 * not have other segments in the geometry obstructing them.
 */
const getEligibleArrayIndices = (
  tool: DesignTool,
  camera: SkyviewCamera,
  positions: Vector3[],
  isPolygon: boolean,
): number[] => {
  const points = positions.map((x, idx) => {
    const screenPosition = camera.getScreenPosition(x);
    return {
      screenPosition,
      /**
       * The index of the point within the flatIndexArray and positions arrays.
       */
      arrayIndex: idx,
      distance: screenPosition.distanceTo(tool.lastMouseEvent.pointer.client),
    };
  });

  const segments = points.map((item, idx) => {
    const start = points[idx].screenPosition;
    const end = points[(idx + 1) % points.length].screenPosition;
    const result = findClosestPointOnLine(
      toVector3(start),
      toVector3(end),
      toVector3(tool.lastMouseEvent.pointer.client),
    );
    const midPoint = toVector3(start.clone().add(end).multiplyScalar(0.5));
    const mousePos = toVector3(tool.lastMouseEvent.pointer.client);
    return {
      arrayIndex: item.arrayIndex,
      midPoint,
      distance: result.onLine ? result.point.distanceTo(mousePos) : midPoint.distanceTo(mousePos),
      start,
      end,
    };
  });

  if (!isPolygon) {
    segments.pop();
  }

  /**
   * Checks if the vector towards the closest point on a segment from the cursor
   * intersects with any other segment on the geometry.
   */
  const intersectsSelf = (segment: (typeof segments)[0]) => {
    const line = {
      start: toVector3(tool.lastMouseEvent.pointer.client),
      end: segment.midPoint,
    };

    return segments.some((x) => {
      if (x.arrayIndex === segment.arrayIndex) {
        return false;
      }

      const result = checkLine2dIntersection(line, {
        start: toVector3(x.start),
        end: toVector3(x.end),
      });

      return result.onLine1 && result.onLine2;
    });
  };

  return segments
    .filter((x) => !intersectsSelf(x))
    .sort((a, b) => (a.distance < b.distance ? -1 : 1))
    .map((x) => x.arrayIndex);
};

/**
 * Extracts the vertex based on index.
 */
const getVertex = (tool: DesignTool, index: PointId) => {
  return tool.asset.designNode.getVertexByPointId(index, 'world');
};

/**
 * Gets the curve of each line segment of the polyline / polygon in the order of the points, or
 * undefined where it is straight. The line segment at an index goes from the point at the index
 * to the next.
 */
const getSegmentCurves = (
  tool: DesignTool,
  pointIdArray: PointId[],
  isPolygon: boolean,
): (SegmentCurve | undefined)[] => {
  const segmentCount = isPolygon ? pointIdArray.length : pointIdArray.length - 1;

  return Array.from({ length: segmentCount }, (__, idx) => {
    const from = pointIdArray[idx];
    const to = pointIdArray[(idx + 1) % pointIdArray.length];
    const segment = tool.asset.designNode.findLineSegmentByPointIds(from, to);

    if (!segment?.curve) {
      return undefined;
    }

    // The line segment may go the other way.
    return segment.values[0] === from ? segment.curve : reverseSegmentCurve(segment.curve);
  });
};

/**
 * Gets a flat directional vector for the segment at the array index position, where it starts
 * (t 0) or ends (t 1). For a curved segment it is the direction of the curve there.
 */
const getFlatLineSegmentDir = (
  positions: Vector3[],
  curves: (SegmentCurve | undefined)[],
  arrayIndex: number,
  t: 0 | 1,
) => {
  const start = positions[arrayIndex];
  const end = positions[(arrayIndex + 1) % positions.length];
  const curve = curves[arrayIndex];

  if (!curve) {
    return end.clone().sub(start).setZ(0).normalize();
  }

  const direction = getConicDirection(getSegmentConic(start, end, curve), t);
  return new Vector3(direction.x, direction.y, 0);
};

/**
 * Gets the curves of the offset line segments, that follow the curves they are offset from: like
 * for those, the control point is where the directions at the ends meet, which are the same as
 * before. A circular arc gets a circular arc around the same center.
 *
 * A line segment is straight if its curve can't be offset that far, past the center of an arc.
 */
const getOffsetCurves = (
  positions: Vector3[],
  curves: (SegmentCurve | undefined)[],
  offsetPositions: Vector3[],
): (SegmentCurve | undefined)[] =>
  curves.map((curve, idx) => {
    if (!curve) {
      return undefined;
    }

    const nextIdx = (idx + 1) % positions.length;
    const conic = getSegmentConic(positions[idx], positions[nextIdx], curve);
    const start = offsetPositions[idx];
    const end = offsetPositions[nextIdx];
    const startDir = conic.control.clone().sub(conic.start).setZ(0);
    const endDir = conic.end.clone().sub(conic.control).setZ(0);

    // Where the line from the start along startDir meets the line from the end along endDir.
    const cross = startDir.x * endDir.y - startDir.y * endDir.x;

    if (Math.abs(cross) < 1e-12) {
      return undefined;
    }

    const toEnd = end.clone().sub(start);
    const control = start
      .clone()
      .addScaledVector(startDir, (toEnd.x * endDir.y - toEnd.y * endDir.x) / cross);

    // Past the center of an arc the ends swap sides and the control point is behind them.
    if (
      control.clone().sub(start).dot(startDir) <= 0 ||
      end.clone().sub(control).dot(endDir) <= 0
    ) {
      return undefined;
    }

    // The control point keeps its height above the ends.
    control.setZ(conic.control.z + (start.z - conic.start.z + (end.z - conic.end.z)) / 2);

    return getSegmentCurve({ start, control, end, weight: conic.weight });
  });

const applyOffsetResult = (
  tool: DesignTool,
  vertices: Vector3[],
  isPolygon: boolean,
  curves: (SegmentCurve | undefined)[] = [],
) => {
  let changes: RevertChange[] = [];
  const activeAttributes = tool.getActiveAttributes();
  addAndCommitAction(tool, {
    commit: () => {
      const maxLineIndex = isPolygon ? vertices.length : vertices.length - 1;

      const addPointsResult = addPoints(tool.asset.designNode, vertices, activeAttributes);
      // The changes are in reverse order, for reverting.
      const pointIds = addPointsResult.map((x) => x.id).reverse();

      const addLineSegmentsResult = pointIds
        .slice(0, maxLineIndex)
        .map((id, idx) =>
          tool.asset.designNode.addLineSegment(
            id,
            pointIds[(idx + 1) % pointIds.length],
            activeAttributes,
            curves[idx],
          ),
        );

      changes = [...addLineSegmentsResult, ...addPointsResult];
      tool.asset.designNode.refresh();
      tool.asset.refreshMetadata();
    },
    rollback: () => {
      tool.asset.designNode.revert(changes);
      tool.asset.designNode.refresh();
      tool.asset.refreshMetadata();
    },
  });
};

/**
 * @returns The positions in local position.
 */
const getOffsetConfiguration = (tool: DesignTool, lineIds: LineSegmentId[]) => {
  /**
   * Identify a line or polygon in the geometry based on start point.
   */
  const items = listLinesAndPolygonsBySelection(tool.asset.designNode, lineIds);
  const pointIds = getPointIdArray(items[0].lines);

  return {
    segments: items[0].lines,
    isPolygon: items[0].isPolygon,
    pointIdArray: getPointIdArray(items[0].lines),
    positions: pointIds.map((x) => tool.asset.designNode.getLocalPosition(getVertex(tool, x))),
    curves: getSegmentCurves(tool, pointIds, items[0].isPolygon),
  };
};

/**
 * Gets the ray that represents how the selected data will be offset based on the tool settings.
 */
function getOffsetRays(
  tool: DesignTool,
  offsetConfiguration: ReturnType<typeof getOffsetConfiguration>,
): Ray[] | undefined {
  const camera = cameraManager().getActiveCamera();

  if (!camera) {
    return;
  }

  const raycaster = camera.getRay(
    tool.lastMouseEvent.pointer.position.x,
    tool.lastMouseEvent.pointer.position.y,
  );
  const worldPositions = offsetConfiguration.positions.map((x) =>
    tool.asset.designNode.getWorldPosition(x),
  );

  const eligibleArrayIndices = getEligibleArrayIndices(
    tool,
    camera,
    worldPositions,
    offsetConfiguration.isPolygon,
  );

  if (eligibleArrayIndices.length === 0) {
    return;
  }

  for (const index of eligibleArrayIndices) {
    const result = getOffsetProjections(
      tool,
      raycaster,
      offsetConfiguration.pointIdArray,
      worldPositions,
      offsetConfiguration.isPolygon,
      index,
    );

    if (isDefined(result)) {
      return result.map((x) => x.ray);
    }
  }
}

/**
 * Intersects the offset from start through end: the closest of the snap targets.
 */
function intersectOffsetRay(
  tool: DesignTool,
  start: Vector3,
  end: Vector3,
  pointCloudChunks?: Points[],
): Vector3 | undefined {
  return tool.intersectSnapTargets(start, end, pointCloudChunks);
}

/**
 * Intersects the ray of each vertex of the offset.
 *
 * The horizontal distance and height difference of a vertex without a hit is interpolated from the
 * vertices with a hit, so that the offset does not drop back to the line where there is nothing to
 * intersect. Without any hit, each vertex falls back to a short offset along its ray.
 */
function intersectOffsetRays(
  tool: DesignTool,
  rays: Ray[],
  isPolygon: boolean,
  pointCloudChunks?: Points[],
): Vector3[] {
  const getFallback = (ray: Ray) =>
    ray.origin.clone().add(ray.direction.clone().multiplyScalar(0.1));

  const hits = rays.map((ray) =>
    intersectOffsetRay(tool, ray.origin, getFallback(ray), pointCloudChunks),
  );

  const origins = rays.map((ray) => ray.origin);
  const horizontalDistances = interpolateMissingValues(
    hits.map((hit, index) => hit?.clone().sub(origins[index]).setZ(0).length()),
    origins,
    isPolygon,
  );
  const heightDifferences = interpolateMissingValues(
    hits.map((hit, index) => (isDefined(hit) ? hit.z - origins[index].z : undefined)),
    origins,
    isPolygon,
  );

  return rays.map((ray, index) => {
    const hit = hits[index];

    if (isDefined(hit)) {
      return hit;
    }

    if (!horizontalDistances || !heightDifferences) {
      return getFallback(ray);
    }

    return ray.origin
      .clone()
      .add(ray.direction.clone().setZ(0).normalize().multiplyScalar(horizontalDistances[index]))
      .setZ(ray.origin.z + heightDifferences[index]);
  });
}

function intersectProjectionResult(
  tool: DesignTool,
  rays: Ray[],
  pointCloudChunks?: Points[],
  isPolygon = false,
): Vector3[] {
  return intersectOffsetRays(tool, rays, isPolygon, pointCloudChunks);
}

/**
 * @returns The offset vectors in world position.
 */
const generateOffset = (
  tool: DesignTool,
  pointIdArray: PointId[],
  localPositions: Vector3[],
  isPolygon: boolean,
  pointCloudChunks?: Points[],
) => {
  const camera = cameraManager().getActiveCamera();

  if (!camera) {
    return;
  }

  const worldPositions = localPositions.map((x) => tool.asset.designNode.getWorldPosition(x));
  const eligibleArrayIndices = getEligibleArrayIndices(tool, camera, worldPositions, isPolygon);

  if (eligibleArrayIndices.length === 0) {
    return;
  }

  const raycaster = camera.getRay(
    tool.lastMouseEvent.pointer.position.x,
    tool.lastMouseEvent.pointer.position.y,
  );

  /**
   * Checks if the cursor is on the line when projected onto the screen.
   */
  const isResultOnLine = (projectedLine: Vector3[], index: number) => {
    const start = toVector3(camera.getScreenPosition(projectedLine[index]));
    const end = toVector3(
      camera.getScreenPosition(projectedLine[(index + 1) % projectedLine.length]),
    );

    // The length of the line could end up being 0 when transformed to screen coordinates when zoomed out
    if (start.distanceTo(end) === 0) {
      return false;
    }

    return findClosestPointOnLine(start, end, toVector3(tool.lastMouseEvent.pointer.client)).onLine;
  };

  let vertices: Vector3[] | undefined;
  for (const index of eligibleArrayIndices) {
    const nextProjections = getOffsetProjections(
      tool,
      raycaster,
      pointIdArray,
      worldPositions,
      isPolygon,
      index,
    );

    if (!isDefined(nextProjections)) {
      continue;
    }

    if (
      tool.placeMode === PlaceMode.IntersectPointcloud ||
      tool.placeMode === PlaceMode.IntersectObjects
    ) {
      const results = intersectOffsetRays(
        tool,
        nextProjections.map((projection) => projection.ray),
        isPolygon,
        pointCloudChunks,
      );

      nextProjections.forEach((projection, projectionIndex) => {
        projection.result = results[projectionIndex];
      });
    }

    const nextVectices = nextProjections.map((x) => x.result);

    vertices ??= nextVectices;

    if (isResultOnLine(nextVectices, index)) {
      vertices = nextVectices;
      break;
    }
  }

  return vertices;
};

/**
 * Calculate a flat normal vector between the closest point on the line
 * and where the current snap point is.
 *
 * On a curved segment it is across the curve where it is closest to the cursor, along with the
 * direction of the curve there.
 */
function getFlatSegmentDirAndNormal(
  vec: Vector3,
  index: number,
  raycaster: Raycaster,
  positions: Vector3[],
  curves: (SegmentCurve | undefined)[],
) {
  const cursorPoint = findPointOnLineFromHeight(
    raycaster.ray.origin,
    raycaster.ray.direction,
    vec.z,
  );
  if (!cursorPoint) {
    return;
  }

  const curve = curves[index];

  if (curve) {
    const conic = getSegmentConic(vec, positions[(index + 1) % positions.length], curve);
    const t = getConicParameter(conic, cursorPoint);
    const direction = getConicDirection(conic, t);
    const dir = new Vector3(direction.x, direction.y, 0);
    // Across the curve, also past its ends where the closest point is the end.
    const across = new Vector3(dir.y, -dir.x, 0);

    return {
      normal: across.multiplyScalar(across.dot(cursorPoint.sub(getConicPoint(conic, t)))),
      dir,
    };
  }

  const segmentDir = getFlatLineSegmentDir(positions, curves, index, 0);
  const linePoint = findClosestPointOnRay(vec.clone(), segmentDir, cursorPoint.clone());

  return {
    /**
     * A flat normal vector between the closest point on the line
     * and where the current snap point is.
     */
    normal: new Vector3().subVectors(cursorPoint.clone(), linePoint.clone()),
    dir: segmentDir,
  };
}

/**
 * Gets the horizontal distance to offset a vertex. With a 3D length the length is measured along
 * the offset, including the height difference to the vertex, so the horizontal distance is shorter.
 *
 * Returns undefined if the height difference is longer than the length.
 */
const getHorizontalOffsetDistance = (
  tool: DesignTool,
  distance: number,
  vertexHeight: number,
): number | undefined => {
  if (tool.snapSettings.length !== '3d') {
    return distance;
  }

  let heightDifference: number;

  switch (tool.snapMode) {
    case SnapMode.RelativeSlopePercent:
    case SnapMode.RelativeSlopeFraction: {
      const slope = tool.snapValues.slope * 0.01;
      return distance / Math.sqrt(1 + slope * slope);
    }
    case SnapMode.RelativeHeight:
      heightDifference = tool.snapValues.heightOffset;
      break;
    case SnapMode.AbsoluteHeight:
      heightDifference =
        tool.snapValues.height - CoordinateManager.getInstance().getMAS() - vertexHeight;
      break;
    default:
      return distance;
  }

  if (Math.abs(heightDifference) > distance) {
    return undefined;
  }

  return Math.sqrt(distance * distance - heightDifference * heightDifference);
};

const getOffsetProjections = (
  tool: DesignTool,
  raycaster: Raycaster,
  pointIdArray: PointId[],
  positions: Vector3[],
  isPolygon: boolean,
  baseIndex: number,
) => {
  const curves = getSegmentCurves(tool, pointIdArray, isPolygon);
  const segment = getFlatSegmentDirAndNormal(
    positions[baseIndex],
    baseIndex,
    raycaster,
    positions,
    curves,
  );

  if (!segment) {
    return undefined;
  }

  switch (tool.placeMode) {
    case PlaceMode.Length:
    case PlaceMode.LengthPerpendicular:
      segment.normal.normalize().multiplyScalar(tool.snapValues.length);
  }

  /**
   * A quaterntion representing the rotation between the first line segment
   * and the target vector.
   */
  const q = getQuaternionBetweenVectors(segment.dir, segment.normal);

  // The horizontal offset distance of each vertex, which can differ between vertices with a 3D length.
  const offsetDistances = pointIdArray.map((x) =>
    getHorizontalOffsetDistance(tool, segment.normal.length(), getVertex(tool, x).z),
  );

  if (offsetDistances.some((distance) => !isDefined(distance))) {
    return undefined;
  }

  /**
   * Apply the quaternion to each vertex using the line segments direction vector
   * to find the correct offset position for it.
   */
  const projections = pointIdArray.map((x, idx) => {
    /**
     * Will repeat the first segment if its a line and its the first vertex.
     * (since no segments exists beyond the edge of the line)
     */
    const firstRepeat = idx === 0 && !isPolygon;

    /**
     * Will repeat the last segment if its a line and its the last vertex.
     * (since no segments exists beyond the edge of the line)
     */
    const lastRepeat = idx === pointIdArray.length - 1 && !isPolygon;

    // The segment direction that comes right before the current vertex, at the vertex.
    const previousDir = getFlatLineSegmentDir(
      positions,
      curves,
      firstRepeat ? idx : idx === 0 ? pointIdArray.length - 1 : idx - 1,
      firstRepeat ? 0 : 1,
    );

    // The segment drection that comes right after the current vertex, at the vertex.
    const nextDir = getFlatLineSegmentDir(
      positions,
      curves,
      lastRepeat ? idx - 1 : idx,
      lastRepeat ? 1 : 0,
    );

    /**
     * Get the offset vectors using the quaternion to rotate
     * the directional vector, and targetVector magnitude to
     * give it the correct length.
     */
    const offsetDistance = offsetDistances[idx]!;

    const offsetPrevious = previousDir.clone().applyQuaternion(q).multiplyScalar(offsetDistance);

    const offsetNext = nextDir.clone().applyQuaternion(q).multiplyScalar(offsetDistance);

    const origin = getVertex(tool, x);

    /**
     * The result offset point.
     * If the two line segments have the same direction
     * we only need to apply the offset to the point to
     * get the offset position.
     */
    let result: Vector3 = origin.clone().add(offsetNext);

    /**
     * If the lines are not parallel we need to find the point
     * where an offset line from the last segment and an offset line from the next
     * segment would intersect.
     */
    if (!areVectorsParallel(previousDir, nextDir)) {
      /**
       * Create a raycast that looks in the direction of the resultant between the
       * two offsets. (ignoring z)
       */
      const rayCaster = new Raycaster(
        new Vector3(0, 0, 0),
        offsetPrevious.clone().add(offsetNext).setZ(0).normalize(),
      );

      /**
       * Create the offset line that travels along the previous
       * line segment.
       *
       * The line segment has to be long enough so it will not
       * fail to be intersected by the raycast.
       */
      const line = new PickingLine();
      line.setIndicesAndVertices(
        new Float32BufferAttribute(
          [
            ...offsetPrevious.clone().sub(previousDir.multiplyScalar(1000)).setZ(0).toArray(),
            ...offsetPrevious.clone().add(previousDir.multiplyScalar(1000)).setZ(0).toArray(),
          ],
          3,
        ),
      );

      const intersections = rayCaster.intersectObject(line);

      if (intersections.length > 0) {
        /**
         * The intersection point between the resultant and the offset line
         * is our desired offset position for this vertex.
         */
        result = origin.clone().add(intersections[0].point);
      }
    }

    // Adjust the Z-value of the offset vertex depedning on settings.
    switch (tool.snapMode) {
      case SnapMode.AbsoluteHeight:
        result.setZ(tool.snapValues.height - CoordinateManager.getInstance().getMAS());
        break;
      case SnapMode.RelativeHeight:
        result.setZ(result.z + tool.snapValues.heightOffset);
        break;
      case SnapMode.RelativeSlopePercent:
      case SnapMode.RelativeSlopeFraction:
        result.setZ(result.z + slopePercentToHeightDiff(tool.snapValues.slope, offsetDistance));
        break;
    }

    return { result, ray: new Ray(origin, result.clone().sub(origin).normalize()) };
  });

  /**
   * Do not refresh points if they contain NaN values.
   */
  if (projections.some((v) => !isValidVector3(v.result))) {
    return undefined;
  }

  return projections;
};

export const offsetUtils = {
  getOffsetRays,
  intersectProjectionResult,
  activateCreateOffsetFeature,
  initCreateOffset,
  applyOffsetResult,
  getOffsetConfiguration,
  getOffsetCurves,
  generateOffset,
};
